/**
 * Notifications — the lifecycle rail, as an operator sees it.
 *
 * Spec 2026-09-22 §4.5 (notification rails v2), task 5.2. Reads the five
 * endpoints task 5.1 added in `backend/src/routes/admin-notifications.ts`:
 *
 *   GET  /v1/admin/notifications/overview?days=7|30|90   notifications.read
 *   GET  /v1/admin/notifications/outbox                  notifications.read
 *   GET  /v1/admin/notifications/controls                notifications.read
 *   GET  /v1/admin/notifications/holdout                 notifications.read
 *   POST /v1/admin/notifications/controls/{stage}        notifications.controls.write
 *
 * 🔴 THE PAUSE STOPS THE SEND (wave 7, Task 7.1) — `sendStageEmail` reads the
 * control table and refuses with `stage_paused`, and the complaint circuit
 * breaker writes the same table by itself. The page says so on the control
 * itself and in the card's note; see `data.ts` (`PAUSE_CONTROL_LABEL`,
 * `WAVE_7_NOTE`) and `components/StageControls.tsx`.
 *
 * COUNTS ONLY. No member id, no address, no card copy, no outbox payload
 * crosses this page, because the API sends none.
 *
 * Structure follows the sibling System Health page: `adminFetch` for every
 * call, `tileShell` / `TileUnavailable` for a card that could not be read, and
 * an unreadable card shows an em-dash rather than a zero — a zero is a
 * measurement, "unavailable" is the absence of one.
 */
import { useNotificationsPage } from './useNotificationsPage';
import { RailsTable } from './components/RailsTable';
import { OutboxCard } from './components/OutboxCard';
import { StageControls } from './components/StageControls';
import { HoldoutCard } from './components/HoldoutCard';

export function NotificationsPage() {
  const c = useNotificationsPage();
  // One string for the label and the accessible name, so a screen reader and
  // the screen say the SAME word — two literals drift the moment one changes.
  const refreshLabel = c.refreshing ? 'Refreshing…' : 'Refresh';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Notifications</h2>
          <p className="text-muted-foreground">
            What the lifecycle rail sent, what it is still holding, and which stage emails are
            paused.
          </p>
        </div>
        {/* Disabled only while ITS OWN round trip runs: the first page load is
            not this button's work, and greying it out then says it is. */}
        <button
          type="button"
          onClick={c.refresh}
          disabled={c.refreshing}
          className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          aria-label={refreshLabel}
        >
          {refreshLabel}
        </button>
      </div>

      <RailsTable
        tile={c.overview}
        days={c.days}
        onDaysChange={c.setDays}
        loading={c.overviewLoading}
      />

      <OutboxCard tile={c.outbox} loading={c.restLoading} />

      <StageControls
        tile={c.controls}
        loading={c.restLoading}
        canWrite={c.canWriteControls}
        pending={c.pending}
        reason={c.reason}
        submitting={c.submitting}
        actionError={c.actionError}
        onOpen={c.openControl}
        onClose={c.closeControl}
        onReasonChange={c.setReason}
        onConfirm={() => void c.confirmControl()}
      />

      <HoldoutCard tile={c.holdout} loading={c.restLoading} />
    </div>
  );
}

export default NotificationsPage;
